import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Shield, Trash2, Edit2, Plus, RotateCcw, Search, Filter, Clock, User, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_STYLES = {
  CREATE: { icon: Plus, color: 'text-green-400', bg: 'bg-green-900/20', border: 'border-green-800/40', label: 'Created' },
  EDIT: { icon: Edit2, color: 'text-blue-400', bg: 'bg-blue-900/20', border: 'border-blue-800/40', label: 'Edited' },
  DELETE: { icon: Trash2, color: 'text-red-400', bg: 'bg-red-900/20', border: 'border-red-800/40', label: 'Deleted' },
  RESTORE: { icon: RotateCcw, color: 'text-amber-400', bg: 'bg-amber-900/20', border: 'border-amber-800/40', label: 'Restored' },
};

const AuditLog = () => {
  const { user, token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [deletedLoans, setDeletedLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [detailDialog, setDetailDialog] = useState({ open: false, entry: null });
  const [activeTab, setActiveTab] = useState('log');

  const isMasterAdmin = user?.role === 'master_admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [logRes, deletedRes] = await Promise.all([
        axios.get(`${API}/api/admin/audit-log`, { headers }),
        axios.get(`${API}/api/admin/deleted-loans`, { headers }).catch(() => ({ data: [] })),
      ]);
      setEntries(logRes.data);
      setDeletedLoans(deletedRes.data);
    } catch (err) {
      console.error('Error fetching audit data:', err);
    }
    setLoading(false);
  };

  const handleRestore = async (loanId) => {
    try {
      await axios.post(`${API}/api/admin/restore-loan/${loanId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error restoring loan');
    }
  };

  const filteredEntries = entries.filter(e => {
    if (filterAction !== 'all' && e.action !== filterAction) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        (e.performed_by_name || '').toLowerCase().includes(term) ||
        (e.loan_data?.client_name || '').toLowerCase().includes(term) ||
        (e.loan_data?.user_name || '').toLowerCase().includes(term) ||
        (e.loan_data?.loan_number || '').toLowerCase().includes(term)
      );
    }
    return true;
  });

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return ts; }
  };

  if (user?.role !== 'master_admin' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-xl text-white font-semibold">Access Denied</h2>
          <p className="text-gray-400 text-sm mt-1">Admin access required to view the Audit Log.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-4 md:p-6 lg:p-8" data-testid="audit-log-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <Shield className="w-6 h-6 text-amber-400" />
            <h1 className="text-2xl font-bold text-white">Activity Log</h1>
          </div>
          <p className="text-gray-400 text-sm ml-9">Track all loan changes — who did what, and when</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('log')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'log'
                ? 'bg-amber-900/30 text-amber-400 border border-amber-800/50'
                : 'bg-[#111] text-gray-400 border border-[#222] hover:border-[#333]'
            }`}
            data-testid="tab-activity-log"
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Activity Log ({entries.length})
          </button>
          <button
            onClick={() => setActiveTab('deleted')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'deleted'
                ? 'bg-red-900/30 text-red-400 border border-red-800/50'
                : 'bg-[#111] text-gray-400 border border-[#222] hover:border-[#333]'
            }`}
            data-testid="tab-deleted-loans"
          >
            <Trash2 className="w-4 h-4 inline mr-2" />
            Deleted Loans ({deletedLoans.length})
          </button>
        </div>

        {activeTab === 'log' && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search by name, client, loan #..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-[#111] border border-[#222] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600"
                  data-testid="audit-search"
                />
              </div>
              <div className="flex gap-1.5">
                {['all', 'CREATE', 'EDIT', 'DELETE', 'RESTORE'].map((action) => (
                  <button
                    key={action}
                    onClick={() => setFilterAction(action)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      filterAction === action
                        ? action === 'all'
                          ? 'bg-white/10 text-white border border-white/20'
                          : `${ACTION_STYLES[action]?.bg} ${ACTION_STYLES[action]?.color} border ${ACTION_STYLES[action]?.border}`
                        : 'bg-[#111] text-gray-500 border border-[#222] hover:border-[#333]'
                    }`}
                    data-testid={`filter-${action.toLowerCase()}`}
                  >
                    {action === 'all' ? 'All' : ACTION_STYLES[action]?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Log Entries */}
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading audit log...</div>
            ) : filteredEntries.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No activity recorded yet.</p>
                <p className="text-gray-600 text-sm mt-1">All future loan changes will appear here.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredEntries.map((entry, idx) => {
                  const style = ACTION_STYLES[entry.action] || ACTION_STYLES.EDIT;
                  const Icon = style.icon;
                  return (
                    <div
                      key={entry.id || idx}
                      className={`${style.bg} border ${style.border} rounded-lg p-3 flex items-center gap-3 cursor-pointer hover:brightness-110 transition-all`}
                      onClick={() => setDetailDialog({ open: true, entry })}
                      data-testid={`audit-entry-${idx}`}
                    >
                      <div className={`${style.bg} p-2 rounded-lg`}>
                        <Icon className={`w-4 h-4 ${style.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-sm font-medium">{entry.performed_by_name}</span>
                          <span className={`text-xs font-medium ${style.color}`}>{style.label}</span>
                          <span className="text-gray-400 text-xs">
                            {entry.loan_data?.client_name && `"${entry.loan_data.client_name}"`}
                            {entry.loan_data?.loan_number && ` (${entry.loan_data.loan_number})`}
                          </span>
                        </div>
                        {entry.loan_data?.user_name && entry.loan_data.user_name !== entry.performed_by_name && (
                          <p className="text-gray-500 text-xs mt-0.5">LO: {entry.loan_data.user_name}</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-gray-400 text-xs">{formatTimestamp(entry.timestamp)}</p>
                        <p className="text-gray-600 text-xs">{entry.performed_by_role}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'deleted' && (
          <div>
            {deletedLoans.length === 0 ? (
              <div className="text-center py-12">
                <Trash2 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No deleted loans found.</p>
              </div>
            ) : (
              <div className="bg-[#111] border border-[#222] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#222] text-gray-400 text-xs">
                      <th className="py-3 px-4 text-left font-medium">Loan Officer</th>
                      <th className="py-3 px-4 text-left font-medium">Client</th>
                      <th className="py-3 px-4 text-left font-medium">Loan #</th>
                      <th className="py-3 px-4 text-right font-medium">Amount</th>
                      <th className="py-3 px-4 text-left font-medium">Status</th>
                      <th className="py-3 px-4 text-left font-medium">Deleted By</th>
                      <th className="py-3 px-4 text-left font-medium">Deleted At</th>
                      {isMasterAdmin && <th className="py-3 px-4 text-center font-medium">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {deletedLoans.map((loan, idx) => (
                      <tr key={loan.id} className="border-b border-[#1a1a1a] hover:bg-[#1a1a1a] transition-colors" data-testid={`deleted-loan-${idx}`}>
                        <td className="py-3 px-4 text-white">{loan.user_name}</td>
                        <td className="py-3 px-4 text-gray-300">{loan.client_name}</td>
                        <td className="py-3 px-4 text-gray-400 font-mono text-xs">{loan.loan_number}</td>
                        <td className="py-3 px-4 text-right text-white">${(loan.loan_amount || 0).toLocaleString()}</td>
                        <td className="py-3 px-4">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-400">{loan.status}</span>
                        </td>
                        <td className="py-3 px-4 text-red-400 text-xs">{loan.deleted_by_name || '—'}</td>
                        <td className="py-3 px-4 text-gray-500 text-xs">{formatTimestamp(loan.deleted_at)}</td>
                        {isMasterAdmin && (
                          <td className="py-3 px-4 text-center">
                            <Button
                              size="sm"
                              onClick={() => handleRestore(loan.id)}
                              className="bg-amber-600 hover:bg-amber-700 text-white text-xs px-3 py-1 h-auto"
                              data-testid={`restore-loan-${idx}`}
                            >
                              <RotateCcw className="w-3 h-3 mr-1" />
                              Restore
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => !open && setDetailDialog({ open: false, entry: null })}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Activity Details</DialogTitle>
          </DialogHeader>
          {detailDialog.entry && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-gray-500 text-xs mb-1">Action</p>
                  <p className={`font-medium ${ACTION_STYLES[detailDialog.entry.action]?.color || 'text-white'}`}>
                    {ACTION_STYLES[detailDialog.entry.action]?.label || detailDialog.entry.action}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">Timestamp</p>
                  <p className="text-white">{formatTimestamp(detailDialog.entry.timestamp)}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">Performed By</p>
                  <p className="text-white">{detailDialog.entry.performed_by_name}</p>
                  <p className="text-gray-500 text-xs">{detailDialog.entry.performed_by_email} ({detailDialog.entry.performed_by_role})</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs mb-1">Loan</p>
                  <p className="text-white">{detailDialog.entry.loan_data?.client_name || '—'}</p>
                  <p className="text-gray-500 text-xs">{detailDialog.entry.loan_data?.loan_number || ''}</p>
                </div>
              </div>

              {detailDialog.entry.action === 'DELETE' && detailDialog.entry.loan_data && (
                <div className="bg-red-950/20 border border-red-900/30 rounded-lg p-3 mt-3">
                  <p className="text-red-400 text-xs font-medium mb-2">Deleted Loan Data</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.entries(detailDialog.entry.loan_data).map(([k, v]) => v ? (
                      <div key={k}>
                        <span className="text-gray-500">{k.replace(/_/g, ' ')}:</span>{' '}
                        <span className="text-gray-300">{typeof v === 'number' ? `$${v.toLocaleString()}` : v}</span>
                      </div>
                    ) : null)}
                  </div>
                </div>
              )}

              {detailDialog.entry.action === 'EDIT' && detailDialog.entry.changes && Object.keys(detailDialog.entry.changes).length > 0 && (
                <div className="bg-blue-950/20 border border-blue-900/30 rounded-lg p-3 mt-3">
                  <p className="text-blue-400 text-xs font-medium mb-2">Changes Made</p>
                  <div className="space-y-1.5">
                    {Object.entries(detailDialog.entry.changes).map(([field, change]) => (
                      <div key={field} className="text-xs">
                        <span className="text-gray-400">{field.replace(/_/g, ' ')}:</span>{' '}
                        <span className="text-red-400 line-through">{change.old}</span>{' '}
                        <span className="text-gray-500">→</span>{' '}
                        <span className="text-green-400">{change.new}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLog;
